import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MLoader from "@/components/ui/atoms/MLoader";
import MDialog from "@/components/ui/atoms/MDialog";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { ListItem } from "@/components/ui/atoms/MListItem";
import { IconEdit, IconCircleX, IconPlus } from "@tabler/icons-react";
import styles from "@/pages/documents/document.module.css";
import MInfiniteScroll from "@/components/ui/atoms/MInfiniteScroll";
import { PAGE_SIZE } from "@/utils/constants";
import { MButton } from "@/components/ui/atoms/MButtons";
import IconBubble from "@/components/icons/IconBubble";
import FormHeader from "@/components/ui/organisms/MHeader";
import { getMemberInfo } from "@/hooks/useMembers";
import useHealthMemory from "@/hooks/useHealthMemory";
import { useScrollRestore } from "@/hooks/useScrollRestore";
import MSpacer from "@/components/ui/atoms/MSpacer";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";

// Placeholder types and hooks for demonstration
interface HealthMemory {
  id: string;
  title: string;
  year: number;
  month?: number;
  details: string;
  createdAt?: string;
}

const HealthMemoryList: React.FC = () => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memoryToDelete, setMemoryToDelete] = useState<HealthMemory | null>(
    null
  );
  const navigate = useNavigate();
  const { showToast } = useToast();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;

  const [page, setPage] = useState(2); // next page to fetch
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [memories, setMemories] = useState<HealthMemory[]>([]);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const {
    healthMemories,
    loading,
    error,
    fetchHealthMemories,
    deleteHealthMemory,
  } = useHealthMemory(memberId);

  // Preserve scroll position when navigating away to edit/add a memory and
  // back. Mirrors DocumentListing — saves scroll + paginated item count before
  // leaving, refetches enough pages on return to land back where we were.
  // The hook's `loading` arg must start true and flip false only after the
  // list rows are actually in the DOM, otherwise it would restore on the
  // first render (loader still visible) and mark itself done forever.
  const isStillLoading = !initialLoadDone || isLoading;
  const { saveScroll, saveItemCount, getSavedItemCount } = useScrollRestore(
    "healthMemoryListing",
    isStillLoading
  );

  useEffect(() => {
    if (!memberId) return;
    // (Re)load for selected member, always page 1
    (async () => {
      setIsLoading(true);
      setInitialLoadDone(false);

      setMemories([]);
      setPage(2);
      setHasMore(true);

      const savedCount = getSavedItemCount();

      const data = await fetchHealthMemories({ page: 1, pageSize: PAGE_SIZE });
      let allData: HealthMemory[] = data || [];
      let nextPage = 2;
      let more = allData.length === PAGE_SIZE;

      // If we're returning to the listing after an edit/add, refetch as many
      // pages as we had before so the scroll-restore lands on the right row.
      if (savedCount && savedCount > PAGE_SIZE) {
        while (more && allData.length < savedCount) {
          const moreData = await fetchHealthMemories({
            page: nextPage,
            pageSize: PAGE_SIZE,
          });
          const chunk = moreData || [];
          if (chunk.length === 0) {
            more = false;
            break;
          }
          allData = [...allData, ...chunk];
          nextPage += 1;
          more = chunk.length === PAGE_SIZE;
        }
      }

      setMemories(allData);
      setPage(nextPage);
      setHasMore(more);

      setIsLoading(false);
      setInitialLoadDone(true);

      // Track page load with total memories count
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
        screenName: ANALYTICS_SCREEN_NAMES.HEALTH_MEMORY_HOME,
        additionalUserProperties: {
          total_memories: String(allData.length),
        },
      });
    })();
  }, [memberId]);

  // Infinite scroll fetch
  const fetchNextPage = async () => {
    if (isLoading || !hasMore) return;
    setIsLoading(true);
    const data = await fetchHealthMemories({ page, pageSize: PAGE_SIZE });
    setMemories((prev) => [...prev, ...(data || [])]);
    setPage((prev) => prev + 1);
    setHasMore((data || []).length === PAGE_SIZE);
    setIsLoading(false);
  };

  if (loading) return <MLoader />;
  if (error)
    return (
      <MDialog
        isOpen={true}
        title="Could not load health memory notes"
        description={error}
        primaryCtaText="Retry"
        onPrimaryCta={() => window.location.reload()}
        onClose={() => window.location.reload()}
      />
    );

  return (
    <div
      className="profile-page-root bg-white"
      // `.profile-page-root` adds 140px + safe-area bottom padding so content
      // doesn't get hidden behind the floating footer nav. This route already
      // reserves space for the footer via NoFooterLayout (showFooter), so the
      // extra padding becomes dead scrollable area below the last card.
      style={{ paddingBottom: `env(safe-area-inset-bottom)` }}
    >
      <FormHeader
        subHeading="Health memory"
        module="healthmemory"
        memberInfo={memberInfo}
        showProfilePickerRow
        showGradient
        onAdd={() => {
          saveScroll();
          saveItemCount(memories.length);
          navigate("/documents/health-memory/add");
        }}
        learnMoreContent={
          <>
            Some health experiences never make it into formal records--like an
            injury, a resolved condition, or a special need you or your loved
            ones recall.
            <br />
            <br />
            Capturing them as notes here adds missing context and builds a
            fuller health story to support better care.
          </>
        }
      />

      <MSpacer mt={32} />

      <div className="profile-flex-col-gap" style={{ marginTop: 0 }}>
        {!initialLoadDone || isLoading ? (
          <MLoader />
        ) : memories.length === 0 ? (
          <MEmptyCard
            primaryTitle="No health memory notes yet"
            icon={<IconBubble />}
            primaryCTA={{
              text: "Add health memory note ",
              onClick: () => {
                saveScroll();
                saveItemCount(memories.length);
                navigate("/documents/health-memory/add");
              },
            }}
          />
        ) : (
          <MInfiniteScroll
            data={memories}
            hasMore={hasMore}
            isLoading={isLoading}
            onLoadMore={fetchNextPage}
            renderItem={(memory, idx) => {
              // Format date display: show month and year if month exists, otherwise just year
              const getDateDisplay = () => {
                if (memory.month && memory.year) {
                  const date = new Date(memory.year, memory.month - 1);
                  const monthName = date.toLocaleString("default", {
                    month: "long",
                  });
                  return `In ${monthName} ${memory.year}`;
                }
                return `In ${memory.year?.toString() || ""}`;
              };

              return (
                <div key={memory.id} style={{ position: "relative" }}>
                  <ListItem
                    usage="crud"
                    type="2-line"
                    variant="purple"
                    title={memory.title}
                    secondaryText={getDateDisplay()}
                    actions={[
                      {
                        id: "edit",
                        label: "Edit",
                        icon: <IconEdit size={20} />,
                        onClick: () => {
                          saveScroll();
                          saveItemCount(memories.length);
                          navigate(
                            `/documents/health-memory/edit?mode=edit&id=${memory.id}`
                          );
                        },
                      },
                      {
                        id: "delete",
                        label: "Delete",
                        icon: <IconCircleX size={20} />,
                        onClick: () => {
                          setMemoryToDelete(memory);
                          setShowDeleteModal(true);

                          // Track delete click
                          trackUserDataLoaded({
                            memberInfo,
                            screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
                            screenName:
                              ANALYTICS_SCREEN_NAMES.HEALTH_MEMORY_HOME,
                            action: "delete_click",
                          });
                        },
                        className: "delete",
                      },
                    ]}
                  />
                </div>
              );
            }}
          />
        )}
      </div>

      {showDeleteModal && memoryToDelete && (
        <MDialog
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setMemoryToDelete(null);
          }}
          title="Delete this memory?"
          description="This memory will be permanently removed from your health records."
          primaryCtaText="Yes"
          onPrimaryCta={async () => {
            if (!memoryToDelete) return;
            await deleteHealthMemory(memoryToDelete.id);

            // Track delete confirmation
            trackUserDataLoaded({
              memberInfo,
              screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
              screenName: ANALYTICS_SCREEN_NAMES.HEALTH_MEMORY_HOME,
              action: "delete_confirm",
            });

            setShowDeleteModal(false);
            setMemoryToDelete(null);

            setIsLoading(true);
            const data = await fetchHealthMemories({
              page: 1,
              pageSize: PAGE_SIZE,
            });
            setMemories(data || []);
            setPage(2);
            setHasMore((data || []).length === PAGE_SIZE);
            setIsLoading(false);
          }}
          secondaryCtaText="No"
          onSecondaryCta={() => {
            setShowDeleteModal(false);
            setMemoryToDelete(null);
          }}
        />
      )}
    </div>
  );
};

export default HealthMemoryList;
